import React, { useEffect, useMemo, useState } from 'react';
import './Analytics.css';
import { getLandlordAnalytics, getLandlordPropertyRentAnalytics, getLandlordRentAnalytics } from '../../../api/services/analyticsService';
import { getMyProperties } from '../../../api/services/propertyService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const currency = (value) => {
  const num = safeNumber(value);
  return `KES ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const Analytics = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [portfolioRent, setPortfolioRent] = useState(null);
  const [propertyRent, setPropertyRent] = useState(null);

  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [loadingPropertyRent, setLoadingPropertyRent] = useState(false);
  const [error, setError] = useState(null);

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => currentYear - i);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingSnapshot(true);
      setError(null);
      try {
        const [snapshotResp, propsResp] = await Promise.all([
          getLandlordAnalytics(),
          getMyProperties({ page: 0, size: 200 }),
        ]);

        if (!mounted) return;

        const snap = snapshotResp?.data || null;
        setSnapshot(snap);

        if (snap?.currentYear) setYear(safeNumber(snap.currentYear));
        if (snap?.currentMonth) setMonth(safeNumber(snap.currentMonth));

        const list = propsResp?.data?.content || propsResp?.data || propsResp?.content || [];
        const normalized = Array.isArray(list) ? list : [];
        setProperties(normalized);

        const firstId = normalized?.[0]?.id || normalized?.[0]?.propertyId || '';
        if (firstId) setSelectedPropertyId(String(firstId));
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load landlord analytics');
      } finally {
        if (!mounted) return;
        setLoadingSnapshot(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingPortfolio(true);
      try {
        const resp = await getLandlordRentAnalytics({ year, month });
        if (!mounted) return;
        setPortfolioRent(resp?.data || null);
      } catch (e) {
        if (!mounted) return;
        setPortfolioRent(null);
      } finally {
        if (!mounted) return;
        setLoadingPortfolio(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [year, month]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!selectedPropertyId) {
        setPropertyRent(null);
        return;
      }
      setLoadingPropertyRent(true);
      try {
        const resp = await getLandlordPropertyRentAnalytics({ propertyId: selectedPropertyId, year, month });
        if (!mounted) return;
        setPropertyRent(resp?.data || null);
      } catch (e) {
        if (!mounted) return;
        setPropertyRent(null);
      } finally {
        if (!mounted) return;
        setLoadingPropertyRent(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedPropertyId, year, month]);

  const snap = snapshot || {};
  const rent = portfolioRent || {};
  const propRent = propertyRent || {};

  const periodLabel = `${monthOptions.find(m => m.value === month)?.label || 'Month'} ${year}`;

  return (
    <div className="analytics-container">
      <CollapsibleTopPanel id="landlord-analytics-top-panel" defaultOpenOnMobile={false}>
        <div className="analytics-header">
          <h1 className="page-title">Analytics</h1>
          <div className="analytics-filters">
            <select
              className="month-selector period-selector"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            >
              {monthOptions.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              className="year-selector period-selector"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      {error ? (
        <div className="analytics-alert analytics-alert-error">
          <div className="analytics-alert-title">Couldn’t load analytics</div>
          <div className="analytics-alert-message">{error}</div>
        </div>
      ) : null}
      
      <div className="metrics-row">
        <div className={`metric-card ${loadingSnapshot ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏢</div>
          <div className="metric-content">
            <div className="metric-label">Properties</div>
            <div className="metric-value">{safeNumber(snap.propertiesCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total properties</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loadingSnapshot ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏠</div>
          <div className="metric-content">
            <div className="metric-label">Units</div>
            <div className="metric-value">{safeNumber(snap.unitsCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Total units</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loadingSnapshot ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">👥</div>
          <div className="metric-content">
            <div className="metric-label">Active Tenants</div>
            <div className="metric-value">{safeNumber(snap.activeTenantsCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Currently active</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loadingSnapshot ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">🧑‍💼</div>
          <div className="metric-content">
            <div className="metric-label">Agents Assigned</div>
            <div className="metric-value">{safeNumber(snap.agentsAssignedCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Assigned agents</span>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3 className="chart-title">Rent Portfolio ({periodLabel})</h3>
          <p className="chart-subtitle">Expected vs paid vs pending</p>

          <div className="analytics-mini-grid">
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Expected</div>
              <div className="analytics-mini-value">{currency(rent.expected)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Paid</div>
              <div className="analytics-mini-value">{currency(rent.paid)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Pending</div>
              <div className="analytics-mini-value">{currency(rent.pending)}</div>
            </div>
          </div>

          <div className="analytics-muted">
            {loadingPortfolio ? 'Loading rent stats…' : (rent.byMonth ? 'Monthly breakdown available.' : 'No monthly breakdown available.')}
          </div>
        </div>

        <div className="chart-card">
          <div className="analytics-section-header">
            <div>
              <h3 className="chart-title">Rent by Property ({periodLabel})</h3>
              <p className="chart-subtitle">Select a property to view rent stats</p>
            </div>
            <select
              className="property-selector"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
            >
              <option value="">Select property</option>
              {properties.map((p) => (
                <option key={p.id || p.propertyId} value={p.id || p.propertyId}>
                  {p.name || p.propertyName || p.address || `Property ${p.id || p.propertyId}`}
                </option>
              ))}
            </select>
          </div>

          <div className="analytics-mini-grid">
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Expected</div>
              <div className="analytics-mini-value">{currency(propRent.expected)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Paid</div>
              <div className="analytics-mini-value">{currency(propRent.paid)}</div>
            </div>
            <div className="analytics-mini-item">
              <div className="analytics-mini-label">Pending</div>
              <div className="analytics-mini-value">{currency(propRent.pending)}</div>
            </div>
          </div>

          <div className="analytics-muted">
            {selectedPropertyId ? (loadingPropertyRent ? 'Loading property rent stats…' : (propRent.byMonth ? 'Monthly breakdown available.' : 'No monthly breakdown available.')) : 'Pick a property to see details.'}
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card full-width">
          <h3 className="chart-title">Rent & Maintenance Snapshot</h3>
          <p className="chart-subtitle">Key totals and maintenance status</p>

          <div className="analytics-summary-grid">
            <div className="analytics-summary-item">
              <div className="analytics-mini-label">Total Monthly Payable Rent</div>
              <div className="analytics-summary-value">{currency(snap.totalMonthlyPayableRent)}</div>
            </div>
            <div className="analytics-summary-item">
              <div className="analytics-mini-label">Total Monthly Expected Rent</div>
              <div className="analytics-summary-value">{currency(snap.totalMonthlyExpectedRent)}</div>
            </div>
            <div className="analytics-summary-item">
              <div className="analytics-mini-label">Rent Paid This Month</div>
              <div className="analytics-summary-value">{currency(snap.rentPaidThisMonth)}</div>
            </div>
            <div className="analytics-summary-item">
              <div className="analytics-mini-label">Rent Pending This Month</div>
              <div className="analytics-summary-value">{currency(snap.rentPendingThisMonth)}</div>
            </div>
          </div>

          <div className="analytics-maintenance-grid">
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">Open</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceOpenCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">Handled</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceHandledCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">Completed</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceCompletedCount)}</span>
            </div>
            <div className="analytics-maintenance-item">
              <span className="analytics-maintenance-label">Cancelled</span>
              <span className="analytics-maintenance-value">{safeNumber(snap.maintenanceCancelledCount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
